/**
 * Plan of Care Summary Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { ClipboardList, Download } from "lucide-react";

export function PlanOfCareOverlay({ patientId, patientName, onSave, onClose }) {
  const [poc, setPoc] = useState({
    certification_period: "01/15/2026 - 04/14/2026",
    attending_physician: "Dr. Sarah Johnson, MD",
    hospice_medical_director: "Dr. Michael Chen, MD",
    primary_diagnosis: {
      description: "End-stage heart failure",
      icd10: "I50.9",
      onset_date: "2025-06-15"
    },
    secondary_diagnoses: [
      { description: "Chronic kidney disease, stage 4", icd10: "N18.4" },
      { description: "Type 2 diabetes mellitus", icd10: "E11.9" },
      { description: "Atrial fibrillation", icd10: "I48.91" }
    ],
    prognosis: "Patient has terminal diagnosis with life expectancy of 6 months or less",
    goals: [
      { id: 1, goal: "Patient will remain comfortable with pain controlled at 4/10 or less", status: "in_progress", target_date: "Ongoing" },
      { id: 2, goal: "Patient will maintain dignity and quality of life as defined by patient/family", status: "in_progress", target_date: "Ongoing" },
      { id: 3, goal: "Family will verbalize understanding of disease process and expected changes", status: "met", target_date: "02/01/2026" },
      { id: 4, goal: "Patient will have symptoms managed to allow meaningful interactions", status: "in_progress", target_date: "Ongoing" }
    ],
    interventions: [
      { discipline: "RN", intervention: "Assess and manage pain using 0-10 scale, titrate medications as ordered", frequency: "Each visit" },
      { discipline: "RN", intervention: "Monitor cardiac status, edema, and respiratory symptoms", frequency: "Each visit" },
      { discipline: "RN", intervention: "Educate family on comfort measures and end-of-life changes", frequency: "PRN" },
      { discipline: "MSW", intervention: "Provide psychosocial support and resource coordination", frequency: "Each visit" },
      { discipline: "MSW", intervention: "Assist with advance directive completion/review", frequency: "As needed" },
      { discipline: "Chaplain", intervention: "Spiritual assessment and support per patient wishes", frequency: "Each visit" },
      { discipline: "HHA", intervention: "Assist with personal care, bathing, and ADLs", frequency: "3x/week" }
    ],
    medications: [
      { name: "Morphine Sulfate", dose: "15mg", route: "PO", frequency: "q4h PRN", indication: "Pain" },
      { name: "Furosemide", dose: "40mg", route: "PO", frequency: "Daily", indication: "Edema" },
      { name: "Lorazepam", dose: "0.5mg", route: "SL", frequency: "q6h PRN", indication: "Anxiety" },
      { name: "Docusate", dose: "100mg", route: "PO", frequency: "BID", indication: "Constipation prevention" }
    ],
    dme: [
      { item: "Hospital bed with side rails", status: "In place" },
      { item: "Bedside commode", status: "In place" },
      { item: "Oxygen concentrator", status: "In place" },
      { item: "Wheelchair", status: "In place" }
    ]
  });

  const getGoalStatusBadge = (status) => {
    switch(status) {
      case "met": return <Badge className="bg-green-500">Met</Badge>;
      case "in_progress": return <Badge className="bg-blue-500">In Progress</Badge>;
      case "not_met": return <Badge className="bg-red-500">Not Met</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="plan-of-care-overlay">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-teal-400" />
          <h3 className="font-semibold text-white">Plan of Care Summary</h3>
          <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
        </div>
        <Badge className="bg-teal-600">{poc.certification_period}</Badge>
      </div>

      <ScrollArea className="h-[550px] pr-2">
        <div className="space-y-4">
          {/* Physicians */}
          <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-400 mb-1">Attending Physician</p>
            <p className="font-medium text-white">{poc.attending_physician}</p>
            <p className="text-xs text-slate-400 mt-2 mb-1">Hospice Medical Director</p>
            <p className="font-medium text-white">{poc.hospice_medical_director}</p>
          </div>

          {/* Diagnoses */}
          <div className="space-y-2">
            <Label className="text-teal-400">Active Diagnoses</Label>
            <div className="p-3 bg-slate-800/50 rounded-lg space-y-2 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">{poc.primary_diagnosis.description}</p>
                  <p className="text-xs text-slate-400">Primary Terminal Diagnosis</p>
                </div>
                <Badge variant="outline" className="border-slate-600 text-slate-300">{poc.primary_diagnosis.icd10}</Badge>
              </div>
              <div className="border-t border-slate-700 pt-2 mt-2">
                <p className="text-xs text-slate-400 mb-2">Secondary Diagnoses</p>
                {poc.secondary_diagnoses.map((dx, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1">
                    <p className="text-sm text-slate-200">{dx.description}</p>
                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">{dx.icd10}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Goals */}
          <div className="space-y-2">
            <Label className="text-green-400">Care Goals</Label>
            <div className="space-y-2">
              {poc.goals.map(goal => (
                <div key={goal.id} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm flex-1 text-slate-200">{goal.goal}</p>
                    {getGoalStatusBadge(goal.status)}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Target: {goal.target_date}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Interventions */}
          <div className="space-y-2">
            <Label className="text-blue-400">Current Interventions</Label>
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-400 border-b border-slate-700">
                    <th className="text-left py-2 w-16">Disc.</th>
                    <th className="text-left py-2">Intervention</th>
                    <th className="text-left py-2 w-24">Frequency</th>
                  </tr>
                </thead>
                <tbody>
                  {poc.interventions.map((int, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50">
                      <td className="py-2"><Badge variant="outline" className="text-xs border-slate-600 text-slate-300">{int.discipline}</Badge></td>
                      <td className="py-2 text-slate-200">{int.intervention}</td>
                      <td className="py-2 text-xs text-slate-400">{int.frequency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Medications */}
          <div className="space-y-2">
            <Label className="text-purple-400">Comfort Kit and Medications</Label>
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-400 border-b border-slate-700">
                    <th className="text-left py-2">Medication</th>
                    <th className="text-left py-2">Dose/Route</th>
                    <th className="text-left py-2">Frequency</th>
                    <th className="text-left py-2">For</th>
                  </tr>
                </thead>
                <tbody>
                  {poc.medications.map((med, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50">
                      <td className="py-2 font-medium text-white">{med.name}</td>
                      <td className="py-2 text-slate-200">{med.dose} {med.route}</td>
                      <td className="py-2 text-xs text-slate-400">{med.frequency}</td>
                      <td className="py-2 text-xs text-slate-300">{med.indication}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* DME */}
          <div className="space-y-2">
            <Label className="text-amber-400">DME in Home</Label>
            <div className="flex flex-wrap gap-2">
              {poc.dme.map((item, idx) => (
                <Badge key={idx} variant="outline" className="text-xs border-slate-600 text-slate-300">
                  {item.item} ({item.status})
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Close</Button>
        <Button className="bg-teal-600 hover:bg-teal-700">
          <Download className="w-4 h-4 mr-1" />Export POC
        </Button>
      </div>
    </div>
  );
}

export default PlanOfCareOverlay;
